import { common } from "@mui/material/colors";
import { alpha } from "@mui/material/styles";

import type { SxProps, Theme } from "@mui/material/styles";

/** Décalage de la flèche par rapport au bord du média, en px. */
const EDGE_OFFSET = 8;

/**
 * Habillage des flèches de navigation d'un carousel.
 *
 * Exporté à part du carousel parce que tous les carousels ne partagent pas la
 * même mécanique de défilement, mais doivent tous porter la même flèche :
 * `ImageCarousel` la révèle au survol, le bloc Présentation d'une fiche DMC la
 * laisse visible en permanence.
 *
 * Fond blanc translucide et encre sombre, indépendamment de la palette : la
 * flèche est posée sur une photo, dont on ne connaît ni la teinte ni la
 * luminosité.
 */
export function carouselArrowSx(
  side: "left" | "right",
  {
    alwaysVisible = false,
  }: {
    /**
     * `false` — la flèche n'apparaît qu'au survol du média : sur une vignette
     * de card, elle ne doit pas concurrencer l'image.
     *
     * `true` — toujours visible : sur un média plein cadre, la navigation doit
     * être découvrable sans survol.
     *
     * Un pointeur grossier (`hover: none`) force la visibilité dans les deux
     * cas — sans survol, une flèche cachée est une flèche inaccessible.
     */
    alwaysVisible?: boolean;
  } = {},
): SxProps<Theme> {
  return {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    [side]: EDGE_OFFSET,
    bgcolor: alpha(common.white, 0.9),
    color: alpha(common.black, 0.87),
    opacity: alwaysVisible ? 1 : 0,
    transition: "opacity .15s ease",
    boxShadow: 1,
    "&:hover": { bgcolor: "common.white" },
    "&:focus-visible": { opacity: 1 },
    "@media (hover: none)": { opacity: 1 },
  };
}
